import React from 'react';
import {
  Grid,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(() => ({
  wrap: {
    backgroundColor: '#FFFFFF',
    padding: '4px 0 8px 0',
  },
  fieldLabel: {
    fontWeight: 700,
    color: '#000000',
    fontSize: '0.85rem',
    marginBottom: 4,
  },
  saveBtn: {
    borderRadius: '2rem',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    backgroundColor: '#ec6535',
    color: '#fff',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#000000',
      color: '#fff',
    },
  },
  clearLink: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontSize: '14px',
    color: 'blue',
    marginLeft: '5px',
    '&:hover': {
      color: '#173072',
      textDecoration: 'underline',
    },
  },
}));

const MENU_PROPS = { PaperProps: { style: { maxHeight: 300, overflowY: 'auto' } } };

/**
 * Reusable multi-select filter matching PO Dashboard pattern.
 * options  – array of items
 * value    – array of selected keys
 * onChange – (newSelectedKeys[]) => void
 * getKey   – item → string key (sent to API)
 * getLabel – item → display string
 */
const FilterSelect = ({ value = [], onChange, onClose, options = [], getKey, getLabel }) => {
  const allSelected = options.length > 0 && value.length === options.length;

  const handleChange = (e) => {
    const selected = e.target.value;
    if (selected.includes('__ALL__')) {
      onChange(allSelected ? [] : options.map(getKey));
    } else {
      onChange(selected);
    }
  };

  return (
    <FormControl variant="outlined" size="small" style={{ width: '10rem', marginRight: '1rem' }} className="dashboard-filter-dropdown">
      <Select
        multiple
        fullWidth
        displayEmpty
        value={value}
        onChange={handleChange}
        onClose={onClose}
        renderValue={(sel) => {
          if (!sel || sel.length === 0) return 'All';
          if (sel.length === options.length) return 'All';
          if (sel.length === 1) {
            const match = options.find((o) => getKey(o) === sel[0]);
            return match ? getLabel(match) : sel[0];
          }
          return 'Multiple selection';
        }}
        MenuProps={MENU_PROPS}
      >
        <MenuItem value="__ALL__">
          <Checkbox checked={allSelected} indeterminate={value.length > 0 && !allSelected} />
          <ListItemText primary="All" />
        </MenuItem>
        {options.map((opt) => {
          const k = getKey(opt);
          return (
            <MenuItem key={k} value={k}>
              <Checkbox checked={value.includes(k)} />
              <ListItemText primary={getLabel(opt)} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

const FilterField = ({ label, children }) => {
  const classes = useStyles();
  return (
    <Grid item style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', gap: '10px' }}>
      <Typography className={`dashboard-filter-labels ${classes.fieldLabel}`}>{label}</Typography>
      {children}
    </Grid>
  );
};

const FilterBar = ({
  role,
  filterOptions = {},
  filters = {},
  onFilterChange,
  onFilterCommit,
  onSaveFilter,
  onClearAll,
  rightSlot,
}) => {
  const classes = useStyles();
  const cmos              = filterOptions.cmos || [];
  const consignees        = filterOptions.consignees || [];
  const freightForwarders = filterOptions.freightForwarders || [];
  const brands            = filterOptions.brands || [];
  const sponsors          = filterOptions.sponsors || [];

  return (
    <Grid container spacing={3} alignItems="flex-end" className={classes.wrap}>
      {role === 'CMO' && (
        <FilterField label="Sponsor:">
          <FilterSelect
            value={filters.sponsors || []}
            onChange={(v) => onFilterChange('sponsors', v)}
            onClose={onFilterCommit}
            options={sponsors}
            getKey={(o) => (typeof o === 'string' ? o : o.organizationId || o.organizationName)}
            getLabel={(o) => (typeof o === 'string' ? o : o.organizationName)}
          />
        </FilterField>
      )}

      <FilterField label="CMO - MFG Plant:">
        <FilterSelect
          value={filters.cmos || []}
          onChange={(v) => onFilterChange('cmos', v)}
          onClose={onFilterCommit}
          options={cmos}
          getKey={(o) => (typeof o === 'string' ? o : o.aslNumber)}
          getLabel={(o) => (typeof o === 'string' ? o : `${o.vendorName} - ${o.mfgPlantShortName}`)}
        />
      </FilterField>

      <FilterField label="Consignee:">
        <FilterSelect
          value={filters.consignees || []}
          onChange={(v) => onFilterChange('consignees', v)}
          onClose={onFilterCommit}
          options={consignees}
          getKey={(o) => o.deliveryAddresseeOrgName}
          getLabel={(o) => o.deliveryAddresseeOrgName || o.deliveryAddressID || ''}
        />
      </FilterField>

      <FilterField label="Freight Forwarders:">
        <FilterSelect
          value={filters.freightForwarders || []}
          onChange={(v) => onFilterChange('freightForwarders', v)}
          onClose={onFilterCommit}
          options={freightForwarders}
          getKey={(o) => o.freightForwarderName}
          getLabel={(o) => o.freightForwarderName}
        />
      </FilterField>

      <FilterField label="Brand:">
        <FilterSelect
          value={filters.brands || []}
          onChange={(v) => onFilterChange('brands', v)}
          onClose={onFilterCommit}
          options={brands}
          getKey={(o) => (typeof o === 'string' ? o : o.brand)}
          getLabel={(o) => (typeof o === 'string' ? o : o.brand)}
        />
      </FilterField>

      <Grid item style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', gap: '20px' }}>
        <Button
          variant="contained"
          disableElevation
          disableRipple
          disableFocusRipple
          className={`dashboard-save-filter-btn ${classes.saveBtn}`}
          onClick={onSaveFilter}
        >
          Save Filters
        </Button>
      </Grid>

      <Grid item style={{ display: 'flex' }}>
        <Typography
          variant="caption"
          className={classes.clearLink}
          onClick={onClearAll}
        >
          Clear All Filters
        </Typography>
      </Grid>

      {rightSlot && (
        <Grid
          item
          style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', flexDirection: 'column', marginLeft: 'auto' }}
        >
          {rightSlot}
        </Grid>
      )}
    </Grid>
  );
};

export default FilterBar;
